import React, { useState } from 'react';
import { Calculator as CalcIcon, DollarSign, Home, Wrench } from 'lucide-react';
export function Calculator() {
  const [homeValue, setHomeValue] = useState(810000);
  const [aduCost, setAduCost] = useState(120000);
  const [aduRent, setAduRent] = useState(2200);
  // Simple loan calc for ADU build (Assumes 7% interest, 15 year fixed HELOC/Home Equity Loan)
  const r = 0.07 / 12;
  const n = 15 * 12;
  const monthlyLoanPayment = aduCost * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
  const netCashFlow = aduRent - monthlyLoanPayment;
  return <section className="py-24 bg-white" data-id="element-39">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-40">
        <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-41">
          {/* Left Side - Context */}
          <div className="lg:w-5/12" data-id="element-42">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/10 text-brand-gold px-4 py-1.5 rounded-full mb-6" data-id="element-43">
              <CalcIcon className="w-4 h-4" data-id="element-44" />
              <span className="text-xs font-bold tracking-widest uppercase" data-id="element-45">
                Interactive Tool
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6" data-id="element-46">
              The ADU & Equity Calculator
            </h2>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed" data-id="element-47">
              Many 91311 homes are 30–50 years old with great bones and large
              lots. See how adding an Accessory Dwelling Unit (ADU) can generate
              rental income to offset your mortgage or provide
              multi-generational living space.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-48">
              <li className="flex items-start" data-id="element-49">
                <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm font-bold" data-id="element-50">
                  1
                </div>
                <span className="text-gray-600" data-id="element-51">
                  Leverage your existing home equity to fund the build.
                </span>
              </li>
              <li className="flex items-start" data-id="element-52">
                <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm font-bold" data-id="element-53">
                  2
                </div>
                <span className="text-gray-600" data-id="element-54">
                  Add significant appraised value to your property.
                </span>
              </li>
              <li className="flex items-start" data-id="element-55">
                <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm font-bold" data-id="element-56">
                  3
                </div>
                <span className="text-gray-600" data-id="element-57">
                  Generate monthly cash flow to offset your primary mortgage.
                </span>
              </li>
            </ul>
          </div>

          {/* Right Side - Calculator */}
          <div className="lg:w-7/12 w-full bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-lg" data-id="element-58">
            <div className="space-y-8" data-id="element-59">
              {/* Sliders */}
              <div data-id="element-60">
                <div className="flex justify-between mb-2" data-id="element-61">
                  <label className="text-sm font-medium text-gray-700 flex items-center" data-id="element-62">
                    <Home className="w-4 h-4 mr-2 text-brand-gold" data-id="element-63" /> Current
                    Home Value
                  </label>
                  <span className="font-serif text-lg" data-id="element-64">
                    ${homeValue.toLocaleString()}
                  </span>
                </div>
                <input type="range" min="500000" max="1500000" step="25000" value={homeValue} onChange={e => setHomeValue(Number(e.target.value))} aria-label="Current Home Value" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2" data-id="element-65" />
              </div>

              <div data-id="element-66">
                <div className="flex justify-between mb-2" data-id="element-67">
                  <label className="text-sm font-medium text-gray-700 flex items-center" data-id="element-68">
                    <Wrench className="w-4 h-4 mr-2 text-brand-gold" data-id="element-69" /> ADU
                    Build Cost
                  </label>
                  <span className="font-serif text-lg" data-id="element-70">
                    ${aduCost.toLocaleString()}
                  </span>
                </div>
                <input type="range" min="50000" max="250000" step="5000" value={aduCost} onChange={e => setAduCost(Number(e.target.value))} aria-label="ADU Build Cost" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2" data-id="element-71" />
              </div>

              <div data-id="element-72">
                <div className="flex justify-between mb-2" data-id="element-73">
                  <label className="text-sm font-medium text-gray-700 flex items-center" data-id="element-74">
                    <DollarSign className="w-4 h-4 mr-2 text-brand-gold" data-id="element-75" />{' '}
                    Expected Monthly ADU Rent
                  </label>
                  <span className="font-serif text-lg" data-id="element-76">
                    ${aduRent.toLocaleString()}
                  </span>
                </div>
                <input type="range" min="1000" max="3500" step="100" value={aduRent} onChange={e => setAduRent(Number(e.target.value))} aria-label="Expected Monthly ADU Rent" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2" data-id="element-77" />
              </div>
            </div>

            {/* Results */}
            <div className="mt-10 bg-gray-900 rounded-2xl p-6 text-white" data-id="element-78">
              <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-gray-700" data-id="element-79">
                <div data-id="element-80">
                  <p className="text-gray-400 text-sm mb-1" data-id="element-81">
                    Est. Loan Payment (15yr)
                  </p>
                  <p className="text-2xl font-serif text-red-400" data-id="element-82">
                    -${Math.round(monthlyLoanPayment).toLocaleString()}
                  </p>
                </div>
                <div data-id="element-83">
                  <p className="text-gray-400 text-sm mb-1" data-id="element-84">
                    Monthly ADU Income
                  </p>
                  <p className="text-2xl font-serif text-green-400" data-id="element-85">
                    +${Math.round(aduRent).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-86">
                <p className="text-lg font-medium" data-id="element-87">Net Monthly Cash Flow</p>
                <p className={`text-4xl font-serif ${netCashFlow >= 0 ? 'text-brand-gold' : 'text-white'}`} data-id="element-88">
                  {netCashFlow >= 0 ? '+' : '-'}$
                  {Math.abs(Math.round(netCashFlow)).toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-4 font-light" data-id="element-89">
                *Estimates assume a 7% interest rate on a 15-year fixed home
                equity loan for the build cost. Does not include property tax
                reassessments or maintenance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}